import polyline from "@mapbox/polyline";
import { Marker, Polyline } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import {
  HasLatLong,
  TripResponse,
  TripStopResponse,
  VehicleStopResponse,
  VehicleTripResponse,
} from "types/trip.types";
import CustomerStopMarker from "./CustomerMarker";
import { ColorPalette, ColorSpectrums } from "types/ColorSpectrum";
import RoutePath from "./RoutePath";
import ParkingMarker from "./ParkingMarker";
import { PlaceResponse } from "types/responses.type";
import UnknownPlaceMarker from "./UnknownPlaceMarker";
import HubMarker from "./HubMarker";
import { Tooltip } from "@mantine/core";
interface TripPathProps {
  vehicleTrip?: VehicleTripResponse;
  colorIndex?: number;
  onStopClick?: (stop: VehicleStopResponse) => void;
  onAction?: (action: string, params?: any) => void;
}
export default function TripPath({
  vehicleTrip,
  colorIndex,
  onAction,
}: TripPathProps) {
  const [tripPath, setTripPath] = useState<{ lat: number; lng: number }[]>([]);
  const [returnPath, setReturnPath] = useState<{ lat: number; lng: number }[]>(
    []
  );
  useEffect(() => {
    // Clear path before processing new vehicleTrip
    setTripPath([]);
    setReturnPath([]);
    if (!vehicleTrip?.vehicleStops) return;
    let lastCustomerIndex = vehicleTrip.vehicleStops.length;
    for (let i = lastCustomerIndex - 1; i >= 0; i--) {
      if (vehicleTrip.vehicleStops[i].type == "CUSTOMER") {
        lastCustomerIndex = i;
        break;
      }
    }
    const path: { lat: number; lng: number }[] = [];
    const rPath: { lat: number; lng: number }[] = [];
    vehicleTrip.vehicleStops.forEach((stop, stopIndex) => {
      const stopPoints = polyline
        .decode(stop.polyline)
        .map((p) => ({ lat: p[0], lng: p[1] }));
      if (stopIndex <= lastCustomerIndex) {
        if (stopIndex > 0) path.push(...stopPoints);
      } else {
        rPath.push(...stopPoints);
      }
    });
    setTripPath(path);
    setReturnPath(rPath);
  }, [vehicleTrip]);
  if (!vehicleTrip) return null;

  const handlePathClick = () => {
    onAction && onAction("set-trip-id", { tripId: vehicleTrip.vehicleTripId });
  };

  // Compute label index for CUSTOMER stops only
  let customerIdx = 0;
  const colorSpectrum = ColorPalette[(colorIndex ?? 0) % ColorPalette.length];
  const unmatchedTripStopPlaces: TripStopResponse[] = [];
  const matchedTripStopIds: number[] = [];
  vehicleTrip.vehicleStops?.forEach((vehicleStop) => {
    matchedTripStopIds.push(...(vehicleStop.tripStopIds ?? []));
  });
  vehicleTrip.trips?.forEach((trip) => {
    trip.tripStops?.forEach((stop) => {
      if (
        stop.place &&
        unmatchedTripStopPlaces.findIndex((p) => p.placeId == stop.placeId) <
          0 &&
        matchedTripStopIds.findIndex((tsId) => tsId == stop.tripStopId) < 0
      ) {
        unmatchedTripStopPlaces.push({
          ...stop,
          label:
            (trip.externalId ?? `#${trip.tripId}`) +
            `\n${stop.additionalData?.orders
              ?.map((o: any) => o.externalId)
              .join("\n")}`,
        });
      }
    });
  });

  const visitedTripStopIds: number[] = [];
  if (vehicleTrip.vehicleStops?.length) {
    return (
      <>
        {vehicleTrip.vehicleStops.map((stop, idx) => {
          // let label: string | undefined = undefined;

          stop.mapping
            ?.filter((map) => map.relatedEntity === "TRIP_STOP")
            .forEach((map) => {
              visitedTripStopIds.push(map.relatedEntityId);
            });

          if (stop.type === "CUSTOMER") {
            // label = `${customerIdx++}`;
            // customerIdx++;
            return (
              <CustomerStopMarker
                key={`stop#${idx}`}
                stopIndex={idx}
                stop={stop}
                label={stop.additionalData?.stopLabel ?? `${customerIdx}`}
                color={colorSpectrum}
                onAction={onAction}
                tripId={vehicleTrip.vehicleTripId}
              />
            );
          } else if (stop.type === "HUB") {
            // label = `${customerIdx++}`;
            // customerIdx++;
            return (
              <HubMarker
                key={`stop#${idx}`}
                stopIndex={idx}
                stop={stop}
                color={colorSpectrum}
                onAction={onAction}
                tripId={vehicleTrip.vehicleTripId}
              />
            );
          } else {
            return (
              <ParkingMarker
                key={stop.vehicleStopId ?? idx}
                parking={stop}
                onAction={onAction}
                tripId={vehicleTrip.vehicleTripId}
                // label={}
                // icon={getMarkerIcon(stop.type ?? "")}
              />
            );
          }
        })}
        {unmatchedTripStopPlaces.map((tStop, tStopIdx) => (
          <CustomerStopMarker
            key={tStop.tripStopId}
            stop={tStop.place as any}
            label={`C`}
            stopIndex={tStopIdx}
            color={colorSpectrum}
            onAction={onAction}
            tripId={vehicleTrip.vehicleTripId}
          />
        ))}

        <RoutePath
          points={tripPath}
          color={colorSpectrum}
          onClick={handlePathClick}
        />
        <RoutePath
          points={returnPath}
          color={colorSpectrum}
          onClick={handlePathClick}
          isReturn
        />
      </>
    );
  } else {
    return (
      <>
        {unmatchedTripStopPlaces.map((stop, placeIdx) => (
          <CustomerStopMarker
            key={stop.tripStopId}
            stop={stop.place as any}
            label={`!`}
            tooltip={stop.label}
            stopIndex={placeIdx}
            color={ColorSpectrums.disabled}
            onAction={onAction}
            tripId={vehicleTrip.vehicleTripId}
          />
        ))}
      </>
    );
  }
}
